import { ReactElement, useCallback, useMemo, useState } from "react";

// Third parties
import { useDispatch, useSelector } from "react-redux";

// Evergiving
import {
  CustomInputTag,
  CustomRadioFieldConfig,
} from "@evergiving/common/dist/es2015/form";

// Form components
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { RadioGroup, RadioOptionSpec } from "@/lib/components/RadioGroup";
import { FieldError } from "@/lib/components/FieldError";

// Hooks
import { useShowErrors } from "@/lib/hooks/useShowErrors";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";

// Redux
import {
  resetFormErrors,
  setFormValue,
  setCustomInputFormValue,
} from "@/lib/redux/actions";
import { RootState, VersionedStringFormState } from "@/lib/redux/types";

export function CustomRadioField({
  config,
  name,
}: {
  config: CustomRadioFieldConfig;
  name: keyof VersionedStringFormState;
}): ReactElement {
  const value = useSelector((state: RootState) => state[name].value);
  const dispatch = useDispatch();
  const fieldValidationErrors = useFieldValidationErrors(config);
  const [isDirty, setIsDirty] = useState(false);
  const anyErrors = useShowErrors(config, name, value || "");

  const { requiredFieldErrorText, labelText, options } = config;
  const required = !!requiredFieldErrorText;

  const specs: RadioOptionSpec<string>[] = useMemo(
    () =>
      options.map((option) => ({
        value: option.value,
        content: () => <div>{option.labelText}</div>,
      })),
    [options]
  );

  const handleChange = useCallback(
    (newValue: string) => {
      dispatch(
        setFormValue(
          name,
          newValue,
          fieldValidationErrors.forValue(newValue).length > 0
        )
      );
      dispatch(resetFormErrors(name));

      if (name.includes("customInput_") && newValue) {
        const customInputTag: CustomInputTag = name.split("customInput_")[1];
        dispatch(setCustomInputFormValue(customInputTag, newValue));
      }

      setIsDirty(true);
    },
    [dispatch, fieldValidationErrors, name]
  );

  const errorId = `${name}-errors`;

  return (
    <div>
      <LabelledField id={name} className="customRadioField">
        <LabelText requiredIndicator={required}>{labelText}</LabelText>
        <RadioGroup<string>
          className="custom"
          value={value}
          onChange={handleChange}
          ariaLabel={labelText}
          ariaRequired
          autoFocus
        >
          {specs}
        </RadioGroup>
      </LabelledField>
      {/* eslint-enable jsx-a11y/no-autofocus */}
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(value)}
          />
        ) : null}
      </div>
    </div>
  );
}
